import { HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { QuotesActions } from './quotes.actions';
import { QuotesService } from './quotes.service';
import {
  CANumberById,
  Customer,
  ICableAssemblyOrder,
  Quote,
  QuotePerson,
  QuoteStatus,
} from './quotes.state.interface';

export const loadQuotes$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadActiveQuotes),
      switchMap(({ payload }) =>
        quotesService.getQuotes(payload).pipe(
          map((response: HttpResponse<Quote[]>) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const activeQuotes = response.body || [];

            return QuotesActions.loadActiveQuotesSuccess({ activeQuotes, totalCount: +totalCount });
          }),

          catchError((error) =>
            of(QuotesActions.loadActiveQuotesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadArchivedQuotes$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadArchivedQuotes),
      switchMap(({ payload }) =>
        quotesService.getQuotes(payload).pipe(
          map((response: HttpResponse<Quote[]>) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const archivedQuotes = response.body || [];

            return QuotesActions.loadArchivedQuotesSuccess({
              archivedQuotes,
              totalCount: +totalCount,
            });
          }),

          catchError((error) =>
            of(QuotesActions.loadArchivedQuotesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadAssignees$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadAssignees),
      switchMap(({ payload }) =>
        quotesService.getAssignees(payload).pipe(
          map((assignees: QuotePerson[]) => QuotesActions.loadAssigneesSuccess({ assignees })),

          catchError((error) => of(QuotesActions.loadAssigneesFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadCustomers$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadCustomers),
      switchMap(() =>
        quotesService.getCustomers().pipe(
          map((customers: Customer[]) => QuotesActions.loadCustomersSuccess({ customers })),

          catchError((error) => of(QuotesActions.loadCustomersFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadStatuses$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadStatuses),
      switchMap(({ payload }) =>
        quotesService.getStatuses(payload).pipe(
          map((statuses: QuoteStatus[]) => QuotesActions.loadStatusesSuccess({ statuses })),
          catchError((error) => of(QuotesActions.loadStatusesFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadQuoteShippingMethods$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadQuoteShippingMethod),
      switchMap(() =>
        quotesService.getQuotesShippingMethods().pipe(
          map((quotesShippingMethod) =>
            QuotesActions.loadQuoteShippingMethodSuccess({ quotesShippingMethod }),
          ),
          catchError((error) =>
            of(QuotesActions.loadShippingMethodsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadCANumbers$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadCANumbers),
      switchMap(({ payload }) =>
        quotesService.getCANumbers(payload).pipe(
          map((caNumbers) => QuotesActions.loadCANumbersSuccess({ caNumbers })),
          catchError((error) => of(QuotesActions.loadCANumbersFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadCANumberById$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadCANumberById),
      switchMap(({ id, includeRelations }) =>
        quotesService.getCANumberById(id, includeRelations).pipe(
          map((caNumberById: CANumberById) =>
            QuotesActions.loadCANumberByIdSuccess({ caNumberById }),
          ),
          catchError((error) =>
            of(QuotesActions.loadCANumberByIdFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const addQuote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.addQuote),
      switchMap(({ payload }) =>
        quotesService.addQuote(payload).pipe(
          map((quote: ICableAssemblyOrder) => QuotesActions.addQuoteSuccess({ quote })),
          catchError((error) => of(QuotesActions.addQuoteFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateQuote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.updateQuote),
      switchMap(({ payload, quoteId, initiateRedirect }) =>
        quotesService.updateQuote(payload, quoteId).pipe(
          map((quote: ICableAssemblyOrder) =>
            QuotesActions.updateQuoteSuccess({ quote, initiateRedirect }),
          ),
          catchError((error) => of(QuotesActions.updateQuoteFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateQuoteSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(QuotesActions.updateQuoteSuccess),
      tap((item: { initiateRedirect: boolean; quote: ICableAssemblyOrder }) => {
        snackBar.open('Quote successfully updated.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        if (item.initiateRedirect) {
          router.navigate([`/quotes/${item.quote.id}/view`]).catch(console.error);
        }
      }),
    ),
  { functional: true, dispatch: false },
);

export const updateQuoteFailure = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(QuotesActions.updateQuoteFailure),
      tap(() => {
        snackBar.open('Failed to save quote. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const loadAllItemProcesses$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadItemProcesses),
      switchMap(({ payload }) =>
        quotesService.getItemProcesses(payload).pipe(
          map((itemProcesses) => QuotesActions.loadItemProcessesSuccess({ itemProcesses })),
          catchError((error) =>
            of(QuotesActions.loadItemProcessesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadSpecificItemProcess$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadSpecificItemProcess),
      switchMap(({ id }) =>
        quotesService.getItemProcessById(id).pipe(
          map((selectedItemProcess) =>
            QuotesActions.loadSpecificItemProcessSuccess({ selectedItemProcess }),
          ),
          catchError((error) =>
            of(QuotesActions.loadSpecificItemProcessFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const createQuote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.createQuote),
      switchMap(({ payload }) =>
        quotesService.createQuote(payload).pipe(
          map((createdQuote) => QuotesActions.createQuoteSuccess({ createdQuote })),
          catchError((error) => of(QuotesActions.createQuoteFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const createQuoteSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(QuotesActions.createQuoteSuccess),
      tap(() => {
        snackBar.open('Quote successfully added.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate([`/quotes/active-quotes`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

export const loadSpecificQuote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadSpecificQuote),
      switchMap(({ quoteId }) =>
        quotesService.getQuoteById(quoteId).pipe(
          map((selectedQuote) => QuotesActions.loadSpecificQuoteSuccess({ selectedQuote })),
          catchError((error) =>
            of(QuotesActions.loadSpecificQuoteFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadItemProcessesCategories$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.loadItemProcessesCategories),
      switchMap(() =>
        quotesService.getItemProcessesCategories().pipe(
          map((itemProcessesCategories) =>
            QuotesActions.loadItemProcessesCategoriesSuccess({ itemProcessesCategories }),
          ),
          catchError((error) =>
            of(QuotesActions.loadItemProcessesCategoriesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const saveNote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.saveNote),
      switchMap(({ quoteId, notes }) =>
        quotesService.saveNote(quoteId, notes).pipe(
          map((savedNote) => QuotesActions.saveNoteSuccess({ savedNote })),
          catchError((error) => of(QuotesActions.saveNoteFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const exportOrdersBundle$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.exportQuotesBundle),
      switchMap(({ quotesId }) =>
        quotesService.exportQuotesBundle(quotesId).pipe(
          map((response) => QuotesActions.exportQuotesBundleSuccess({ response })),
          catchError((error) => of(QuotesActions.exportQuotesBundleFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const exportOrdersBundleSuccess$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(QuotesActions.exportQuotesBundleSuccess),
      tap(({ response }) => {
        if (response?.url) {
          window.open(response.url, '_blank');
        }
        snackBar.open('Export orders bundle successful!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const deleteQuote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.deleteQuote),
      switchMap(({ id }) =>
        quotesService.deleteQuote(id).pipe(
          map(() => QuotesActions.deleteQuoteSuccess()),
          catchError((error) => of(QuotesActions.deleteQuoteFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const deleteQuoteSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(QuotesActions.deleteQuoteSuccess),
      tap(() => {
        router.navigate([`/quotes/active-quotes`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

export const archiveQuote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.archiveQuote),
      switchMap(({ id, archived }) =>
        quotesService.archiveQuote(id, archived).pipe(
          map((archivedQuote) => QuotesActions.archiveQuoteSuccess({ archivedQuote })),
          catchError((error) => of(QuotesActions.archiveQuoteFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const archiveQuoteSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(QuotesActions.archiveQuoteSuccess),
      tap(() => {
        snackBar.open('Quote successfully archived.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate([`/quotes/archived-quotes`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

export const unarchiveQuote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.unarchiveQuote),
      switchMap(({ id, archived }) =>
        quotesService.unarchiveQuote(id, archived).pipe(
          map((unarchivedQuote) => QuotesActions.unarchiveQuoteSuccess({ unarchivedQuote })),
          catchError((error) => of(QuotesActions.unarchiveQuoteFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const unarchiveQuoteSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(QuotesActions.unarchiveQuoteSuccess),
      tap(() => {
        snackBar.open('Quote successfully unarchived.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate([`/quotes/active-quotes`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

export const duplicateQuote$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(QuotesActions.duplicateQuote),
      switchMap(({ quoteId }) =>
        quotesService.duplicateQuote(quoteId).pipe(
          map((quoteId: { id: number }) => QuotesActions.duplicateQuoteSuccess({ quoteId })),
          catchError((error) => of(QuotesActions.duplicateQuoteFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const duplicateQuoteSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(QuotesActions.duplicateQuoteSuccess),
      tap(({ quoteId }) => {
        snackBar.open('Quote successfully duplicated.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate([`/quotes/${quoteId.id}/view`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);
